import { defineStore } from 'pinia'
import teamService from '@/services/team.service'
import { useTariffStore } from '@/store/tariffStore'
import {
    DEFAULT_TEAM_ROLE,
    createPermissionsFromRole,
    getRoleByPermissions,
    mapCollaboratorFromBackend,
    mapViewerPermissionsToTeamRole,
    normalizeBackendPermissions,
} from '@/constants/teamPermissions'

export const useTeamStore = defineStore('team', {
    state: () => ({
        members: [],
        projects: [],
        activeQuizIds: [],
        isMembersLoaded: false,
        isMembersLoading: false,
        isMembersSaving: false,
        lastMembersLoadedAt: null,
        incomingAccesses: [],
        isIncomingAccessesLoaded: false,
        isIncomingAccessesLoading: false,
        lastIncomingAccessesLoadedAt: null,
    }),

    getters: {
        allProjectQuizIds(state) {
            return state.projects.flatMap(project => {
                return (project.quizzes || []).map(quiz => quiz.id)
            })
        },
    },

    actions: {
        setProjects(projects = []) {
            this.projects = projects
        },

        setActiveQuizIds(quizIds = []) {
            this.activeQuizIds = [...new Set(quizIds)].filter(Boolean)
            this.isMembersLoaded = false
        },

        getMembersTargetQuizIds(quizIds = []) {
            const targetQuizIds = quizIds.length ? quizIds : this.activeQuizIds
            return [...new Set(targetQuizIds)].filter(Boolean)
        },

        createEmptyMember() {
            return {
                id: `new-${Date.now()}-${Math.random().toString(16).slice(2)}`,
                email: '',
                originalEmail: '',
                name: '',
                role: DEFAULT_TEAM_ROLE,
                originalRole: DEFAULT_TEAM_ROLE,
                permissions: createPermissionsFromRole(DEFAULT_TEAM_ROLE),
                selectedProjectIds: [],
                selectedQuizIds: [],
                originalSelectedQuizIds: [],
                collaboratorIdsByQuiz: {},
                statusesByQuiz: {},
                expiresAt: null,
                status: 'draft',
                isNew: true,
                isEditing: true,
                isSaving: false,
                isRoleOpen: false,
                isAccessOpen: false,
                isFrozen: false,
            }
        },

        addEmptyMember() {
            const hasNewMember = this.members.some(member => member.isNew)
            if (hasNewMember) {
                return null
            }

            const member = this.createEmptyMember()
            this.closeAllMemberDropdowns()
            this.members.push(member)

            return member
        },

        removeMemberLocally(memberId) {
            this.members = this.members.filter(member => member.id !== memberId)
        },

        getAggregateRoleFromQuizIds(member = {}, quizIds = []) {
            const roleKeys = [...new Set(
                quizIds
                    .map(quizId => member.rolesByQuiz?.[quizId])
                    .filter(Boolean)
            )]

            if (!roleKeys.length) {
                return member.role || DEFAULT_TEAM_ROLE
            }

            if (roleKeys.length === 1) {
                return roleKeys[0]
            }

            return 'mixed'
        },

        getAggregatePermissionsFromQuizIds(member = {}, quizIds = []) {
            return quizIds.reduce((acc, quizId) => {
                const permissions = member.permissionsByQuiz?.[quizId] || {}

                Object.entries(permissions).forEach(([key, value]) => {
                    acc[key] = Boolean(acc[key]) || Boolean(value)
                })

                return acc
            }, {})
        },

        groupCollaboratorsByUser(collaborators = []) {
            const membersMap = new Map()

            collaborators
                .filter(collaborator => collaborator?.status !== 'revoked')
                .forEach(collaborator => {
                    const memberKey = collaborator.user_email

                    if (!memberKey) {
                        return
                    }

                    const permissions = normalizeBackendPermissions(collaborator)
                    const role = getRoleByPermissions(permissions)

                    if (!membersMap.has(memberKey)) {
                        const normalizedMember = mapCollaboratorFromBackend(collaborator)

                        membersMap.set(memberKey, {
                            ...normalizedMember,
                            id: `member-${memberKey}`,
                            originalEmail: normalizedMember.email,
                            originalRole: normalizedMember.role,
                            selectedProjectIds: [],
                            selectedQuizIds: [],
                            originalSelectedQuizIds: [],
                            collaboratorIdsByQuiz: {},
                            statusesByQuiz: {},
                            permissionsByQuiz: {},
                            rolesByQuiz: {},
                            invitedByEmailsByQuiz: {},
                            invitedByEmails: [],
                            delegatedMembers: [],
                            isNew: false,
                            isEditing: false,
                            isSaving: false,
                            isRoleOpen: false,
                            isAccessOpen: false,
                            isFrozen: normalizedMember.status === 'frozen',
                        })
                    }

                    const member = membersMap.get(memberKey)

                    if (collaborator.quiz_id && !member.selectedQuizIds.includes(collaborator.quiz_id)) {
                        member.selectedQuizIds.push(collaborator.quiz_id)
                    }

                    if (collaborator.quiz_id && !member.originalSelectedQuizIds.includes(collaborator.quiz_id)) {
                        member.originalSelectedQuizIds.push(collaborator.quiz_id)
                    }

                    if (collaborator.quiz_id) {
                        member.collaboratorIdsByQuiz[collaborator.quiz_id] = collaborator.id
                        member.statusesByQuiz[collaborator.quiz_id] = collaborator.status
                        member.permissionsByQuiz[collaborator.quiz_id] = permissions
                        member.rolesByQuiz[collaborator.quiz_id] = role

                        if (collaborator.invited_by_user_email) {
                            member.invitedByEmailsByQuiz[collaborator.quiz_id] = collaborator.invited_by_user_email

                            if (!member.invitedByEmails.includes(collaborator.invited_by_user_email)) {
                                member.invitedByEmails.push(collaborator.invited_by_user_email)
                            }
                        }
                    }
                })

            const members = Array.from(membersMap.values()).map(member => {
                const statuses = Object.values(member.statusesByQuiz || {})
                member.isFrozen = statuses.length > 0 && statuses.every(status => status === 'frozen')
                member.status = member.isFrozen ? 'frozen' : 'accepted'
                member.role = this.getAggregateRoleFromQuizIds(member, member.selectedQuizIds)
                member.originalRole = member.role
                member.permissions = this.getAggregatePermissionsFromQuizIds(member, member.selectedQuizIds)
                return member
            })

            members.forEach(member => {
                member.delegatedMembers = members.flatMap(candidate => {
                    if (candidate.email === member.email) {
                        return []
                    }

                    const delegatedQuizIds = (candidate.selectedQuizIds || []).filter(quizId => {
                        return candidate.invitedByEmailsByQuiz?.[quizId] === member.email
                    })

                    if (!delegatedQuizIds.length) {
                        return []
                    }

                    return [{
                        ...candidate,
                        id: `${candidate.id}-delegated-by-${member.email}`,
                        selectedProjectIds: [],
                        selectedQuizIds: delegatedQuizIds,
                        originalSelectedQuizIds: delegatedQuizIds,
                        role: this.getAggregateRoleFromQuizIds(candidate, delegatedQuizIds),
                        permissions: this.getAggregatePermissionsFromQuizIds(candidate, delegatedQuizIds),
                        parentEmail: member.email,
                    }]
                })
            })

            return members
        },

        mapIncomingAccessFromQuiz(quiz = {}) {
            const viewerPermissions = quiz.viewer_permissions || {}
            const owner = viewerPermissions.owner || {}
            const inviter = viewerPermissions.inviter || owner || {}
            const collaborator = viewerPermissions.collaborator || {}
            const permissions = normalizeBackendPermissions(viewerPermissions)
            const role = mapViewerPermissionsToTeamRole(viewerPermissions)

            return {
                id: `incoming-${quiz.id}`,
                quizId: quiz.id,
                quizName: quiz.name || '',
                projectId: quiz.project_id || quiz.project?.id || null,
                projectName: quiz.project?.name || '',
                projectEmoji: quiz.project?.emoji || '',
                owner: {
                    id: owner.id || '',
                    email: owner.email || '',
                    name: owner.nickname || owner.name || owner.email || '',
                    avatar: owner.avatar || '',
                },
                inviter: {
                    id: inviter.id || '',
                    email: inviter.email || '',
                    name: inviter.nickname || inviter.name || inviter.email || '',
                    avatar: inviter.avatar || '',
                },
                collaborator: {
                    id: collaborator.id || '',
                    email: collaborator.email || '',
                    name: collaborator.nickname || collaborator.name || collaborator.email || '',
                    avatar: collaborator.avatar || '',
                },
                role,
                permissions,
                isFrozen: false,
            }
        },

        async fetchIncomingAccesses({ force = false } = {}) {
            if (this.isIncomingAccessesLoading) {
                return
            }

            if (this.isIncomingAccessesLoaded && !force) {
                return
            }

            this.isIncomingAccessesLoading = true

            try {
                const quizzes = await teamService.fetchIncomingAccesses()
                this.incomingAccesses = quizzes.map(quiz => this.mapIncomingAccessFromQuiz(quiz))
                this.isIncomingAccessesLoaded = true
                this.lastIncomingAccessesLoadedAt = Date.now()
            } finally {
                this.isIncomingAccessesLoading = false
            }
        },

        async fetchMembers({ force = false, quizIds = [] } = {}) {
            if (this.isMembersLoading) {
                return
            }

            if (this.isMembersLoaded && !force) {
                return
            }

            const targetQuizIds = this.getMembersTargetQuizIds(quizIds)

            if (!targetQuizIds.length) {
                this.members = []
                this.isMembersLoaded = true
                return
            }

            this.isMembersLoading = true

            try {
                const collaborators = await teamService.quizCollaboratorsBatch(targetQuizIds)
                this.members = this.groupCollaboratorsByUser(collaborators)
                this.isMembersLoaded = true
                this.lastMembersLoadedAt = Date.now()
            } finally {
                this.isMembersLoading = false
            }
        },

        refreshTeamUsage() {
            useTariffStore().refreshEffectiveAccess().catch((error) => {
                console.log(error)
            })
        },

        async refreshMembers() {
            await this.fetchMembers({
                force: true,
                quizIds: this.activeQuizIds,
            })
        },

        async shareMemberWithoutRefresh(member, quizIds = []) {
            if (!member || member.isSaving) {
                return []
            }

            member.isSaving = true
            this.isMembersSaving = true

            try {
                const result = await teamService.shareQuizBatch(member, quizIds)
                this.refreshTeamUsage()

                return result
            } finally {
                member.isSaving = false
                this.isMembersSaving = false
            }
        },

        async updateMemberAccessStatus(permissionIds = [], status = 'accepted') {
            const uniquePermissionIds = [...new Set(permissionIds)].filter(Boolean)

            if (!uniquePermissionIds.length) {
                return []
            }

            this.isMembersSaving = true

            try {
                const result = await teamService.updateQuizAccessStatusBatch(uniquePermissionIds, status)
                this.refreshTeamUsage()
                await this.refreshMembers()

                return result
            } finally {
                this.isMembersSaving = false
            }
        },

        async revokeMemberAccess(permissionIds = []) {
            const uniquePermissionIds = [...new Set(permissionIds)].filter(Boolean)

            if (!uniquePermissionIds.length) {
                return []
            }

            this.isMembersSaving = true

            try {
                const result = await teamService.revokeQuizAccessBatch(uniquePermissionIds)
                this.refreshTeamUsage()
                await this.refreshMembers()

                return result
            } finally {
                this.isMembersSaving = false
            }
        },

        closeAllMemberDropdowns() {
            this.members.forEach(member => {
                member.isRoleOpen = false
                member.isAccessOpen = false
            })
        },

        closeOtherMemberDropdowns(activeMemberId) {
            this.members.forEach(member => {
                if (member.id !== activeMemberId) {
                    member.isRoleOpen = false
                    member.isAccessOpen = false
                }
            })
        },

        resetTeamStore() {
            this.members = []
            this.projects = []
            this.activeQuizIds = []
            this.isMembersLoaded = false
            this.isMembersLoading = false
            this.isMembersSaving = false
            this.lastMembersLoadedAt = null
            this.incomingAccesses = []
            this.isIncomingAccessesLoaded = false
            this.isIncomingAccessesLoading = false
            this.lastIncomingAccessesLoadedAt = null
        },
    },
})